import { useEffect, useState } from "react";
import {
  Button,
  message,
  Table,
  Flex,
  Input,
  Spin,
  Switch,
  Modal,
  Tooltip,
  Descriptions,
  Popconfirm,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  FolderOpenOutlined,
  ApiOutlined,
  SearchOutlined,
  CloudDownloadOutlined,
  StopOutlined,
  PauseCircleOutlined,
} from "@ant-design/icons";
import {
  deleteMachine,
  fetchMachine,
  pauseResumeApiCall,
} from "../Service/machine";
import { useNavigate } from "react-router-dom";
import { CgNotes } from "react-icons/cg";

let searchTimeout = null;

const Machine = () => {
  const [machines, setMachines] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("createdDate");
  const [sortOrder, setSortOrder] = useState("descend");
  const [loading, setLoading] = useState(false);
  const [gatewayRecord, setGatewayRecord] = useState(null);
  const [isGatewayModalOpen, setIsGatewayModalOpen] = useState(false);

  const [mqttConnectRecord, setMqttConnectRecord] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigate = useNavigate();

  // ================= FETCH =================
  const fetchData = async (
    pageNum = page,
    pageSize = size,
    searchText = search,
    field = sortField,
    order = sortOrder
  ) => {
    setLoading(true);
    try {
      const res = await fetchMachine({
        page: pageNum - 1,
        size: pageSize,
        search: searchText || "",
        sort: field,
        desc: order === "descend",
        pagination: true,
      });

      setMachines(res.data.content || []);
      setTotal(res.data.totalElements || 0);
      setPage(pageNum);
      setSize(pageSize);
      setSortField(field);
      setSortOrder(order);
    } catch (err) {
      message.error("Error fetching machine list");
    } finally {
      setLoading(false);
    }
  };
  const showGatewayModal = (gateway) => {
    setGatewayRecord(gateway);
    setIsGatewayModalOpen(true);
  };

  const closeGatewayModal = () => {
    setIsGatewayModalOpen(false);
    setGatewayRecord(null);
  };

  // ================= SEARCH DEBOUNCE =================
  useEffect(() => {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      fetchData(1, size, search);
    }, 500);

    return () => clearTimeout(searchTimeout);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, []);

  // ================= DELETE =================
  const deleteData = async (uuid) => {
    try {
      await deleteMachine(uuid);
      message.success("Machine deleted successfully");
      fetchData();
    } catch {
      message.error("Error deleting machine");
    }
  };

  // ================= MQTT =================
  const showModal = (record) => {
    setMqttConnectRecord(record);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setMqttConnectRecord(null);
    fetchData();
  };

  const connectFn = async () => {
    setLoading(true);
    try {
      await pauseResumeApiCall(mqttConnectRecord.uuid, true);
      message.success("Resumed successfully");
      handleCancel();
    } catch {
      message.error("Resume data saving failed");
    } finally {
      setLoading(false);
    }
  };

  const disConnectFn = async () => {
    setLoading(true);
    try {
      await pauseResumeApiCall(mqttConnectRecord.uuid, false);
      message.success("Paused successfully");
      handleCancel();
    } catch {
      message.error("Pause data saving failed");
    } finally {
      setLoading(false);
    }
  };

  // ================= COLUMNS =================
  const columns = [
    {
      title: "Machine Name",
      dataIndex: "assetName",
      key: "assetName",
      sorter: true,
      sortOrder: sortField === "assetName" ? sortOrder : null,
    },
    {
      title: "Organization",
      dataIndex: ["firm", "name"],
      key: "firm",
      sorter: true,
      sortOrder: sortField === "firm" ? sortOrder : null,
    },
    {
      title: "IoT Gateway",
      key: "mqttClient",
      sorter: true,
      render: (_, record) => (
        <Flex justify="space-between" align="center">
          {record.ioTGateway?.brokerName}
          <Button
            type="link"
            onClick={() => showGatewayModal(record.ioTGateway)}
          >
            <CgNotes />
          </Button>
        </Flex>
      ),
    },

    {
      title: "Prefix",
      dataIndex: "mqttPrefix",
      key: "mqttPrefix",
      sorter: true,
      sortOrder: sortField === "mqttPrefix" ? sortOrder : null,
    },
    {
      title: "Connection status",
      dataIndex: "connectionStatus",
      key: "connectionStatus",
      render: (value) => {
        return (
          value && (
            <Flex justify="center">
              <img
                alt="connection status"
                src={`${process.env.PUBLIC_URL}/images/download.gif`}
                style={{ width: "5rem" }}
              />
            </Flex>
          )
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val) => (
        <Switch
          checked={val == true}
          disabled
          checkedChildren="Active"
          unCheckedChildren="In-active"
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        // <>
        //   <Tooltip title="MQTT Connection">
        //     <ApiOutlined
        //       disabled={!record.subscriptionStatus}
        //       onClick={() => showModal(record)}
        //       style={{ marginRight: 12, cursor: "pointer" }}
        //     />
        //   </Tooltip>
        //   <FolderOpenOutlined
        //     style={{ marginRight: 12, cursor: "pointer" }}
        //     onClick={() => navigate(`view/${record.uuid}`)}
        //   />
        //   <EditOutlined
        //     disabled={record.subscriptionStatus}
        //     style={{ marginRight: 12, cursor: "pointer" }}
        //     onClick={() => navigate(`edit/${record.uuid}`)}
        //   />
        //   <DeleteOutlined
        //     disabled={record.subscriptionStatus}
        //     style={{ cursor: "pointer" }}
        //     onClick={() => deleteData(record.uuid)}
        //   />
        // </>
        <>
          <Tooltip title="Data save resume / pause">
            <ApiOutlined
              onClick={
                record?.ioTGateway?.subscriptionStatus
                  ? () => showModal(record)
                  : undefined
              }
              style={{
                marginRight: 12,
                cursor: record?.ioTGateway?.subscriptionStatus
                  ? "pointer"
                  : "not-allowed",
                color: record?.ioTGateway?.subscriptionStatus
                  ? undefined
                  : "#bfbfbf",
              }}
            />
          </Tooltip>

          <FolderOpenOutlined
            style={{ marginRight: 12, cursor: "pointer" }}
            onClick={() => navigate(`view/${record.uuid}`)}
          />

          <EditOutlined
            onClick={
              record.connectionStatus
                ? undefined
                : () => navigate(`edit/${record.uuid}`)
            }
            style={{
              marginRight: 12,
              cursor: record.connectionStatus ? "not-allowed" : "pointer",
              color: record.connectionStatus ? "#bfbfbf" : undefined,
            }}
          />

          {/* <DeleteOutlined
            onClick={
              record.connectionStatus
                ? undefined
                : () => deleteData(record.uuid)
            }
            style={{
              cursor: record.connectionStatus ? "not-allowed" : "pointer",
              color: record.connectionStatus ? "#bfbfbf" : undefined,
            }}
          /> */}
          <Popconfirm
            title="Delete this item?"
            description="Do you want to delete this machine?"
            okText="Yes"
            cancelText="No"
            onConfirm={() => deleteData(record.uuid)}
            disabled={record.connectionStatus} // 🔥 disables popconfirm itself
          >
            <DeleteOutlined
              style={{
                cursor: record.connectionStatus ? "not-allowed" : "pointer",
                color: record.connectionStatus ? "#bfbfbf" : "#ff4d4f",
              }}
              onClick={(e) => e.stopPropagation()} // prevents row click
            />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ width: "100%" }}>
        <Flex
          justify="space-between"
          align="center"
          style={{ marginBottom: 16 }}
        >
          <div style={{ fontSize: "1rem", fontWeight: 700 }}>
            Machine Management
          </div>
          <Flex gap={8}>
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("add")}
            >
              Add
            </Button>
          </Flex>
        </Flex>

        <Table
          rowKey="uuid"
          columns={columns}
          dataSource={machines}
          bordered
          scroll={{ x: "max-content" }}
          pagination={{
            current: page,
            pageSize: size,
            total,
            showSizeChanger: true,
          }}
          onChange={(pagination, filters, sorter) => {
            const field = sorter.field || sortField;
            const order = sorter.order || sortOrder;

            fetchData(
              pagination.current,
              pagination.pageSize,
              search,
              field,
              order
            );
          }}
        />

        <Modal
          title={mqttConnectRecord?.assetName}
          open={isModalOpen}
          footer={false}
          width={400}
          // centered
          onCancel={handleCancel}
        >
          <img
            alt="connection status"
            src={`${process.env.PUBLIC_URL}/images/${
              mqttConnectRecord?.connectionStatus ? "download.gif" : "pause.jpg"
            }`}
            style={{ width: "100%" }}
          />

          <Button
            type="primary"
            // danger
            icon={
              mqttConnectRecord?.connectionStatus ? (
                <PauseCircleOutlined />
              ) : (
                <CloudDownloadOutlined />
              )
            }
            style={{ width: "100%", marginTop: 10 }}
            onClick={
              mqttConnectRecord?.connectionStatus ? disConnectFn : connectFn
            }
          >
            {mqttConnectRecord?.connectionStatus ? "Pause" : "Resume"}
          </Button>
        </Modal>
        <Modal
          title="IoT Gateway Details"
          open={isGatewayModalOpen}
          onCancel={closeGatewayModal}
          footer={null}
        >
          {gatewayRecord && (
            // <div style={{ lineHeight: "1.8" }}>
            //   <p>
            //     <b>Broker Name:</b> {gatewayRecord.brokerName}
            //   </p>
            //   <p>
            //     <b>Broker URL:</b> {gatewayRecord.brokerUrl}
            //   </p>
            //   <p>
            //     <b>Server Port:</b> {gatewayRecord.serverPort}
            //   </p>
            //   <p>
            //     <b>Client ID:</b> {gatewayRecord.clientId}
            //   </p>
            //   <p>
            //     <b>Subscribe Topic:</b> {gatewayRecord.subTopicName}
            //   </p>
            //   <p>
            //     <b>Publish Topic:</b> {gatewayRecord.pubTopicName}
            //   </p>

            //   <p>
            //     <b>Status:</b>{" "}
            //     <Switch checked={gatewayRecord.status} disabled />
            //   </p>

            //   <p>
            //     <b>Connection Status:</b>{" "}
            //     <Switch checked={gatewayRecord.connectionStatus} disabled />
            //   </p>

            //   <p>
            //     <b>Subscription Status:</b>{" "}
            //     <Switch checked={gatewayRecord.subscriptionStatus} disabled />
            //   </p>
            // </div>

            <Descriptions column={1} size="small" bordered={true}>
              <Descriptions.Item label="Broker Name">
                {gatewayRecord.brokerName}
              </Descriptions.Item>

              <Descriptions.Item label="Broker URL">
                {gatewayRecord.brokerUrl}
              </Descriptions.Item>

              <Descriptions.Item label="Server Port">
                {gatewayRecord.serverPort}
              </Descriptions.Item>

              <Descriptions.Item label="Client ID">
                {gatewayRecord.clientId}
              </Descriptions.Item>

              <Descriptions.Item label="Subscribe Topic">
                {gatewayRecord.subTopicName}
              </Descriptions.Item>

              <Descriptions.Item label="Publish Topic">
                {gatewayRecord.pubTopicName}
              </Descriptions.Item>

              <Descriptions.Item label="Connection Status">
                <Switch
                  checked={gatewayRecord.connectionStatus}
                  disabled
                  checkedChildren="Connected"
                  unCheckedChildren="Disconnected"
                />
              </Descriptions.Item>

              <Descriptions.Item label="Subscription Status">
                <Switch
                  checked={gatewayRecord.subscriptionStatus}
                  disabled
                  checkedChildren="Subscribed"
                  unCheckedChildren="Unsubscribed"
                />
              </Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </div>
    </Spin>
  );
};

export default Machine;
